import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  ToggleLeft, 
  ToggleRight,
  Save,
  X,
  Check,
  AlertTriangle,
  Star,
  Sparkles,
  Gift,
  Settings,
  Copy,
  Download,
  Upload,
  Search,
  Link,
  Link2,
  RefreshCw,
  Info
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import AdminSectionWrapper from './AdminSectionWrapper';

const SpecialDaysManagement = () => {
  const [specialDays, setSpecialDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDays, setFilteredDays] = useState([]);
  const [syncInfo, setSyncInfo] = useState({});
  const [syncLoading, setSyncLoading] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    is_active: false,
    effects_enabled: false,
    coupon_code: '',
    coupon_discount: 0,
    coupon_description: '',
    background_effects: {
      particles: true,
      flag_colors: true,
      fireworks: true,
      divine_colors: false,
      lotus_effects: false,
      modak_animations: false,
      krishna_colors: false,
      flute_effects: false,
      peacock_animations: false,
      victory_colors: false,
      fire_effects: false,
      arrow_animations: false,
      triumph_effects: false,
      diwali_colors: false,
      diya_effects: false,
      lantern_animations: false,
      rangoli_effects: false,
      christmas_colors: false,
      snow_effects: false,
      tree_animations: false,
      star_effects: false,
      gift_animations: false,
      photo_colors: false,
      camera_effects: false,
      aperture_animations: false,
      flash_effects: false,
      rakhi_colors: false,
      thread_effects: false,
      love_animations: false,
      bond_effects: false,
      friendship_colors: false,
      handshake_effects: false,
      smile_animations: false,
      unity_effects: false,
      earth_colors: false,
      leaf_effects: false,
      nature_animations: false,
      eco_effects: false,
      environment_colors: false,
      planet_effects: false,
      conservation_animations: false,
      sustainability_effects: false,
      valentine_colors: false,
      heart_effects: false,
      rose_animations: false,
      love_effects: false,
      cupid_effects: false,
      ram_colors: false,
      bow_effects: false,
      divine_animations: false,
      blessing_effects: false,
      bakrid_colors: false,
      crescent_effects: false,
      prayer_animations: false,
      ramzan_colors: false,
      spiritual_effects: false,
      republic_colors: false,
      parade_animations: false,
      patriotic_effects: false,
      shiva_colors: false,
      trident_effects: false,
      meditation_effects: false,
      holi_colors: false,
      color_effects: false,
      water_animations: false,
      joy_effects: false,
      gulal_effects: false,
      digital_colors: false,
      tech_effects: false,
      analytics_animations: false,
      growth_effects: false,
      sankranthi_colors: false,
      harvest_effects: false,
      kite_animations: false,
      bonfire_effects: false,
      cattle_effects: false,
      rangoli_effects: false,
      pongal_effects: false,
      newyear_colors: false,
      fireworks_effects: false,
      countdown_animations: false,
      celebration_effects: false,
      sparkle_effects: false,
      balloon_effects: false,
      champagne_effects: false,
      clock_effects: false,
      star_effects: false
    },
    animation_settings: {
      header_animation: true,
      button_effects: true,
      scroll_effects: true,
      blessing_effects: false,
      divine_glow: false,
      moonlight_effects: false,
      victory_glow: false,
      fireworks_effects: false,
      light_glow: false,
      sparkle_effects: false,
      snow_glow: false,
      jingle_effects: false,
      photo_glow: false,
      shutter_effects: false,
      love_glow: false,
      heart_effects: false,
      friendship_glow: false,
      joy_effects: false,
      nature_glow: false,
      green_effects: false,
      planet_glow: false,
      eco_effects: false,
      romance_glow: false,
      love_sparkles: false,
      sacred_effects: false,
      peace_effects: false,
      patriotic_glow: false,
      tricolor_effects: false,
      color_glow: false,
      festival_effects: false,
      tech_glow: false,
      innovation_effects: false,
      harvest_glow: false,
      festival_effects: false,
      kite_effects: false,
      bonfire_glow: false,
      celebration_glow: false,
      fireworks_animations: false,
      countdown_effects: false,
      party_effects: false,
      midnight_effects: false
    },
    custom_css: '',
    custom_js: ''
  });

  useEffect(() => {
    fetchSpecialDays();
  }, []);

  // Filter special days based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDays(specialDays);
    } else {
      const filtered = specialDays.filter(day =>
        day.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        day.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        day.coupon_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDays(filtered);
    }
  }, [searchTerm, specialDays]);

  // Debug formData changes
  useEffect(() => {
    if (showModal) {
      console.log('Form data updated:', formData);
    }
  }, [formData, showModal]);

  const fetchSpecialDays = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSpecialDays();
      setSpecialDays(response.data.data || []);
    } catch (error) {
      console.error('Error fetching special days:', error);
      showMessage('Failed to fetch special days', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      is_active: false,
      effects_enabled: false,
      coupon_code: '',
      coupon_discount: 0,
      coupon_description: '',
      sync_with_promotions: true,
      background_effects: {
        particles: true,
        flag_colors: true,
        fireworks: true,
        divine_colors: false,
        lotus_effects: false,
        modak_animations: false,
        krishna_colors: false,
        flute_effects: false,
        peacock_animations: false
      },
      animation_settings: {
        header_animation: true,
        button_effects: true,
        scroll_effects: true,
        blessing_effects: false,
        divine_glow: false,
        moonlight_effects: false
      },
      custom_css: '',
      custom_js: ''
    });
    setEditingDay(null);
  };

  const openModal = (day = null) => {
    if (day) {
      // Format dates for HTML date input (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      console.log('Opening modal with day data:', day);
      console.log('Original start_date:', day.start_date);
      console.log('Original end_date:', day.end_date);
      console.log('Formatted start_date:', formatDateForInput(day.start_date));
      console.log('Formatted end_date:', formatDateForInput(day.end_date));

      setFormData({
        ...day,
        start_date: formatDateForInput(day.start_date),
        end_date: formatDateForInput(day.end_date),
        background_effects: typeof day.background_effects === 'string' 
          ? JSON.parse(day.background_effects) 
          : day.background_effects,
        animation_settings: typeof day.animation_settings === 'string' 
          ? JSON.parse(day.animation_settings) 
          : day.animation_settings
      });
      setEditingDay(day);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    // Don't reset form immediately to avoid clearing dates
    setTimeout(() => {
      resetForm();
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Submitting form data:', formData);
      console.log('Form data is_active:', formData.is_active);
      console.log('Form data effects_enabled:', formData.effects_enabled);
      
      if (editingDay) {
        const response = await adminAPI.updateSpecialDay(editingDay.id, formData);
        console.log('Update response:', response);
        showMessage('Special day updated successfully');
      } else {
        const response = await adminAPI.createSpecialDay(formData);
        console.log('Create response:', response);
        showMessage('Special day created successfully');
      }
      
      closeModal();
      fetchSpecialDays();
      
      // Trigger refresh of special day effects on the website with a small delay
      setTimeout(() => {
        window.dispatchEvent(new Event('specialDayEffectsRefresh'));
      }, 500);
    } catch (error) {
      console.error('Error saving special day:', error);
      showMessage('Failed to save special day', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this special day?')) return;
    
    try {
      await adminAPI.deleteSpecialDay(id);
      showMessage('Special day deleted successfully');
      fetchSpecialDays();
      
      // Trigger refresh of special day effects on the website with a small delay
      setTimeout(() => {
        window.dispatchEvent(new Event('specialDayEffectsRefresh'));
      }, 500);
    } catch (error) {
      console.error('Error deleting special day:', error);
      showMessage('Failed to delete special day', 'error');
    }
  };

  const handleToggle = async (id, field) => {
    try {
      console.log(`Toggling ${field} for special day ${id}`);
      const response = await adminAPI.toggleSpecialDay(id, field);
      console.log('Toggle response:', response);
      console.log('Toggle response data:', response.data);
      showMessage(`${field.replace('_', ' ')} toggled successfully`);
      fetchSpecialDays();
      
      // Trigger refresh of special day effects on the website with a small delay
      setTimeout(() => {
        window.dispatchEvent(new Event('specialDayEffectsRefresh'));
      }, 500);
    } catch (error) {
      console.error('Error toggling special day:', error);
      showMessage('Failed to toggle special day', 'error');
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedDays.length === 0) return;
    
    try {
      if (bulkAction === 'delete') {
        if (!window.confirm(`Are you sure you want to delete ${selectedDays.length} special days?`)) return;
        await adminAPI.bulkDeleteSpecialDays(selectedDays);
        showMessage(`${selectedDays.length} special days deleted successfully`);
      } else if (bulkAction === 'activate') {
        for (const id of selectedDays) {
          await adminAPI.toggleSpecialDay(id, 'is_active');
        }
        showMessage(`${selectedDays.length} special days activated`);
      } else if (bulkAction === 'deactivate') {
        for (const id of selectedDays) {
          await adminAPI.toggleSpecialDay(id, 'is_active');
        }
        showMessage(`${selectedDays.length} special days deactivated`);
      } else if (bulkAction === 'enable_effects') {
        for (const id of selectedDays) {
          await adminAPI.toggleSpecialDay(id, 'effects_enabled');
        }
        showMessage(`Effects enabled for ${selectedDays.length} special days`);
      } else if (bulkAction === 'disable_effects') {
        for (const id of selectedDays) {
          await adminAPI.toggleSpecialDay(id, 'effects_enabled');
        }
        showMessage(`Effects disabled for ${selectedDays.length} special days`);
      } else if (bulkAction === 'sync_on') {
        if (!window.confirm(`Are you sure you want to enable sync for ${selectedDays.length} special days?`)) return;
        const response = await adminAPI.bulkSyncOnSpecialDays(selectedDays);
        const { successful, failed, skipped } = response.data.data;
        let message = `Bulk sync enable completed. ${successful.length} successful`;
        if (failed.length > 0) message += `, ${failed.length} failed`;
        if (skipped.length > 0) message += `, ${skipped.length} skipped`;
        showMessage(message, failed.length > 0 ? 'warning' : 'success');
      } else if (bulkAction === 'sync_off') {
        if (!window.confirm(`Are you sure you want to disable sync for ${selectedDays.length} special days?`)) return;
        const response = await adminAPI.bulkSyncOffSpecialDays(selectedDays);
        const { successful, failed, skipped } = response.data.data;
        let message = `Bulk sync disable completed. ${successful.length} successful`;
        if (failed.length > 0) message += `, ${failed.length} failed`;
        if (skipped.length > 0) message += `, ${skipped.length} skipped`;
        showMessage(message, failed.length > 0 ? 'warning' : 'success');
      }
      
      setSelectedDays([]);
      setBulkAction('');
      fetchSpecialDays();
      
      // Trigger refresh of special day effects on the website with a small delay
      setTimeout(() => {
        window.dispatchEvent(new Event('specialDayEffectsRefresh'));
      }, 500);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      showMessage('Failed to perform bulk action', 'error');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDays(filteredDays.map(day => day.id));
    } else {
      setSelectedDays([]);
    }
  };

  const handleSelectDay = (id, checked) => {
    if (checked) {
      setSelectedDays(prev => [...prev, id]);
    } else {
      setSelectedDays(prev => prev.filter(dayId => dayId !== id));
    }
  };

  const isActiveToday = (startDate, endDate) => {
    const today = new Date().toISOString().split('T')[0];
    return today >= startDate && today <= endDate;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Sync-related functions
  const fetchSyncInfo = async (id) => {
    try {
      const response = await adminAPI.getSpecialDaySyncInfo(id);
      setSyncInfo(prev => ({
        ...prev,
        [id]: response.data
      }));
    } catch (error) {
      console.error('Error fetching sync info:', error);
    }
  };

  const handleSyncToggle = async (id) => {
    try {
      console.log('🔄 Starting sync toggle for special day:', id);
      console.log('📡 Making API call to toggleSpecialDay with field: sync_with_promotions');
      
      const response = await adminAPI.toggleSpecialDay(id, 'sync_with_promotions');
      
      console.log('✅ Toggle response received:', response);
      console.log('📊 Response data:', response.data);
      
      showMessage('Sync with promotions toggled successfully');
      
      // Add a small delay to ensure backend processing is complete
      setTimeout(() => {
        fetchSpecialDays();
      }, 1000);
      
      // Trigger refresh of special day effects on the website with a small delay
      setTimeout(() => {
        window.dispatchEvent(new Event('specialDayEffectsRefresh'));
      }, 500);
    } catch (error) {
      console.error('❌ Error toggling sync:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      showMessage('Failed to toggle sync', 'error');
    }
  };

  const handleManualSync = async (id) => {
    try {
      setSyncLoading(prev => ({ ...prev, [id]: true }));
      await adminAPI.syncSpecialDay(id);
      showMessage('Special day synchronized with promotion successfully');
      fetchSpecialDays();
      fetchSyncInfo(id);
    } catch (error) {
      console.error('Error syncing special day:', error);
      showMessage('Failed to sync special day', 'error');
    } finally {
      setSyncLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const testSyncStatus = async (id) => {
    try {
      console.log('🧪 Testing sync status for special day:', id);
      const response = await adminAPI.testSpecialDaySync(id);
      console.log('🧪 Test response:', response.data);
      showMessage(`Sync status: ${response.data.data.sync_with_promotions ? 'ON' : 'OFF'}`, 'success');
    } catch (error) {
      console.error('❌ Error testing sync status:', error);
      showMessage('Failed to test sync status', 'error');
    }
  };

  const getSyncStatus = (day) => {
    if (!day.coupon_code) return 'no-coupon';
    if (!day.sync_with_promotions) return 'disabled';
    if (day.promotion_id) return 'synced';
    return 'not-synced';
  };

  const getSyncStatusText = (status) => {
    switch (status) {
      case 'no-coupon': return 'No Coupon Code';
      case 'disabled': return 'Sync Disabled';
      case 'synced': return 'Synced with Promotion';
      case 'not-synced': return 'Not Synced';
      default: return 'Unknown';
    }
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'no-coupon': return 'text-gray-500';
      case 'disabled': return 'text-orange-500';
      case 'synced': return 'text-green-500';
      case 'not-synced': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
            Special Days Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage special day events, effects, and promotional offers
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Special Day
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          messageType === 'error' 
            ? 'bg-red-100 border border-red-400 text-red-700 dark:bg-red-900 dark:border-red-700 dark:text-red-200'
            : 'bg-green-100 border border-green-400 text-green-700 dark:bg-green-900 dark:border-green-700 dark:text-green-200'
        }`}>
          <div className="flex items-center gap-2">
            {messageType === 'error' ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            {message}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search Special Days
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, description, or coupon code..."
                className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setSearchTerm('')}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Found {filteredDays.length} special day(s) matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedDays.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedDays.length} item(s) selected
            </span>
            <div className="flex items-center gap-2">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Action</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
                <option value="enable_effects">Enable Effects</option>
                <option value="disable_effects">Disable Effects</option>
                <option value="sync_on">Sync On</option>
                <option value="sync_off">Sync Off</option>
                <option value="delete">Delete</option>
              </select>
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className={`px-3 py-1 rounded-md text-sm transition-colors text-white ${
                  bulkAction === 'delete' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : bulkAction === 'sync_on' || bulkAction === 'sync_off'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                } disabled:bg-gray-400`}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Special Days List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedDays.length === filteredDays.length && filteredDays.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Effects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Coupon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Sync Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDays.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {searchTerm ? `No special days found matching "${searchTerm}"` : 'No special days found'}
                  </td>
                </tr>
              ) : (
                filteredDays.map((day) => (
                <tr key={day.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day.id)}
                      onChange={(e) => handleSelectDay(day.id, e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {day.title}
                        </div>
                        {day.description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {day.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatDate(day.start_date)} - {formatDate(day.end_date)}
                    </div>
                    {isActiveToday(day.start_date, day.end_date) && (
                      <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                        Active Today
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggle(day.id, 'is_active')}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                        day.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {day.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {day.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggle(day.id, 'effects_enabled')}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                        day.effects_enabled
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <Sparkles className="w-3 h-3" />
                      {day.effects_enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {day.coupon_code ? (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {day.coupon_code}
                        </div>
                        {day.coupon_discount > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {day.coupon_discount}% off
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">No coupon</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {(() => {
                        const syncStatus = getSyncStatus(day);
                        return (
                          <div className="flex items-center gap-2">
                            <span className={`${getSyncStatusColor(syncStatus)} flex items-center gap-1`}>
                              {syncStatus === 'synced' && <Link className="w-3 h-3" />}
                              {syncStatus === 'not-synced' && <Link2 className="w-3 h-3" />}
                              {syncStatus === 'disabled' && <X className="w-3 h-3" />}
                              {syncStatus === 'no-coupon' && <Info className="w-3 h-3" />}
                              {getSyncStatusText(syncStatus)}
                            </span>
                            {day.coupon_code && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleSyncToggle(day.id)}
                                  className={`text-xs px-2 py-1 rounded ${
                                    day.sync_with_promotions 
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                  }`}
                                  title={day.sync_with_promotions ? 'Disable Sync' : 'Enable Sync'}
                                >
                                  {day.sync_with_promotions ? 'ON' : 'OFF'}
                                </button>
                                <button
                                  onClick={() => testSyncStatus(day.id)}
                                  className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/40"
                                  title="Test Sync Status"
                                >
                                  🧪
                                </button>
                                {day.sync_with_promotions && syncStatus === 'not-synced' && (
                                  <button
                                    onClick={() => handleManualSync(day.id)}
                                    disabled={syncLoading[day.id]}
                                    className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/40 disabled:opacity-50"
                                    title="Manual Sync"
                                  >
                                    {syncLoading[day.id] ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-3 h-3" />
                                    )}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openModal(day)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(day.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
        
        {filteredDays.length === 0 && !searchTerm && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No special days</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new special day event.
            </p>
            <div className="mt-6">
              <button
                onClick={() => openModal()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Add Special Day
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4" style={{ zIndex: 99999 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingDay ? 'Edit Special Day' : 'Add Special Day'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Independence Day"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Brief description of the special day"
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleInputChange('end_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                </div>

                {/* Status Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Active Status
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Enable this special day
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleInputChange('is_active', !formData.is_active)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.is_active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Effects Enabled
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Show special effects on website
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleInputChange('effects_enabled', !formData.effects_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.effects_enabled ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.effects_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Coupon Information */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Coupon Settings
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:p-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Coupon Code
                      </label>
                      <input
                        type="text"
                        value={formData.coupon_code}
                        onChange={(e) => handleInputChange('coupon_code', e.target.value.toUpperCase())}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., INDIA15"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Discount Percentage
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.coupon_discount}
                        onChange={(e) => handleInputChange('coupon_discount', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="15"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Coupon Description
                      </label>
                      <input
                        type="text"
                        value={formData.coupon_description}
                        onChange={(e) => handleInputChange('coupon_description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Get 15% off on all services"
                      />
                    </div>
                  </div>
                  
                  {/* Sync Settings */}
                  <div className="mt-6 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Sync with Promotions
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Automatically create/update promotion when coupon code is added
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleInputChange('sync_with_promotions', !formData.sync_with_promotions)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.sync_with_promotions ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.sync_with_promotions ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Effects Settings */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Effects Settings
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Background Effects
                      </h4>
                      <div className="space-y-3">
                        {Object.entries(formData.background_effects).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between">
                            <label className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                              {key.replace('_', ' ')}
                            </label>
                            <button
                              type="button"
                              onClick={() => handleInputChange(`background_effects.${key}`, !value)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                value ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-700'
                              }`}
                            >
                              <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                  value ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Animation Settings
                      </h4>
                      <div className="space-y-3">
                        {Object.entries(formData.animation_settings).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between">
                            <label className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                              {key.replace('_', ' ')}
                            </label>
                            <button
                              type="button"
                              onClick={() => handleInputChange(`animation_settings.${key}`, !value)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                value ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                              }`}
                            >
                              <span
                                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                  value ? 'translate-x-5' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Code */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Custom Code
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Custom CSS
                      </label>
                      <textarea
                        value={formData.custom_css}
                        onChange={(e) => handleInputChange('custom_css', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                        placeholder="/* Custom CSS styles */"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Custom JavaScript
                      </label>
                      <textarea
                        value={formData.custom_js}
                        onChange={(e) => handleInputChange('custom_js', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                        placeholder="// Custom JavaScript code"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    {editingDay ? 'Update' : 'Create'} Special Day
                  </button>
                                 </div>
               </form>
             </div>
           </div>
         </div>,
         document.body
       )}
    </AdminSectionWrapper>
  );
};

export default SpecialDaysManagement;
